import { UcoastEl } from '@/scripts/core/UcoastEl'
import { TsDOM as q } from '@/scripts/core/TsDOM'

type BallState = {
	x: number
	y: number
	vx: number
	vy: number
	radius: number
	rotation: number
}

type PointerSample = {
	x: number
	y: number
	time: number
}

type HintState = {
	isVisible: boolean
	timeoutId?: number
}

const DEFAULT_GRAVITY = 2600
const DEFAULT_BOUNCE = 0.72
const DEFAULT_FRICTION = 0.99
const AIR_DRAG = 0.999
const FLOOR_FRICTION = 0.992
const MAX_TIMESTEP = 1 / 30
const MAX_RELEASE_SPEED = 2600
const SETTLE_SPEED = 18
const SETTLE_BOUNCE_SPEED = 46
const THROW_SAMPLE_WINDOW_MS = 140
const HINT_DELAY_MS = 30000
const HINT_GAP = 20
const MIN_VIEWPORT_INSET = 12

export class PasswordTennisBall extends UcoastEl {
	static override htmlSelector = 'password-tennis-ball'

	private readonly ballEl: HTMLElement
	private readonly hintEl: HTMLElement
	private readonly prefersReducedMotion: boolean
	private readonly pointerSamples: PointerSample[]
	private readonly hint: HintState
	private readonly state: BallState

	private rafId?: number
	private lastFrameTime?: number
	private boundsWidth = 0
	private boundsHeight = 0
	private gravity = DEFAULT_GRAVITY
	private bounce = DEFAULT_BOUNCE
	private friction = DEFAULT_FRICTION
	private pointerId?: number
	private dragOffsetX = 0
	private dragOffsetY = 0
	private isDragging = false
	private hasDropped = false
	private hasInteracted = false

	constructor() {
		super()
		this.ballEl = q.rs('.password-tennis-ball__ball', this)
		this.hintEl = q.rs('.password-tennis-ball__hint', this)
		this.prefersReducedMotion = window.matchMedia(
			'(prefers-reduced-motion: reduce)'
		).matches
		this.pointerSamples = []
		this.hint = {
			isVisible: false,
		}
		this.state = {
			x: 0,
			y: 0,
			vx: 0,
			vy: 0,
			radius: 0,
			rotation: 0,
		}
	}

	override connectedCallback() {
		super.connectedCallback()
		this.readConfig()
		this.updateBounds()
		this.syncBallRadius()
		this.resetBallPosition()
		this.render()
		this.ballEl.addEventListener('pointerdown', this.onPointerDown)
		window.addEventListener('pointermove', this.onPointerMove)
		window.addEventListener('pointerup', this.onPointerUp)
		window.addEventListener('pointercancel', this.onPointerUp)
		window.addEventListener('resize', this.onResize, { passive: true })
		this.scheduleHint()

		if (!this.prefersReducedMotion && !this.hasDropped) {
			this.hasDropped = true
			this.state.vx = 180
			this.state.vy = 0
			this.startLoop()
		}
	}

	override disconnectedCallback() {
		super.disconnectedCallback()
		this.stopLoop()
		this.clearHintTimeout()
		this.ballEl.removeEventListener('pointerdown', this.onPointerDown)
		window.removeEventListener('pointermove', this.onPointerMove)
		window.removeEventListener('pointerup', this.onPointerUp)
		window.removeEventListener('pointercancel', this.onPointerUp)
		window.removeEventListener('resize', this.onResize)
	}

	private readConfig() {
		this.gravity = this.readNumber('gravity', DEFAULT_GRAVITY, 600, 5000)
		this.bounce =
			this.readNumber('bounce', DEFAULT_BOUNCE * 100, 20, 98) / 100
		this.friction =
			this.readNumber('friction', DEFAULT_FRICTION * 100, 85, 100) / 100
	}

	private readNumber(
		key: string,
		fallback: number,
		min: number,
		max: number
	): number {
		const rawValue = this.dataset[key]
		const parsedValue = rawValue ? Number.parseFloat(rawValue) : NaN
		if (Number.isNaN(parsedValue)) return fallback
		return Math.min(max, Math.max(min, parsedValue))
	}

	private updateBounds() {
		this.boundsWidth = window.visualViewport?.width || window.innerWidth
		this.boundsHeight = window.visualViewport?.height || window.innerHeight
	}

	private syncBallRadius() {
		const rectDiameter = this.ballEl.getBoundingClientRect().width
		const inlineDiameter = Number.parseFloat(
			window.getComputedStyle(this.ballEl).width
		)
		const diameter = rectDiameter || inlineDiameter || this.ballEl.offsetWidth
		this.state.radius = diameter > 0 ? diameter / 2 : 0
	}

	private resetBallPosition() {
		const inset = this.state.radius + MIN_VIEWPORT_INSET
		this.state.x = inset
		if (this.prefersReducedMotion) {
			this.state.y = this.boundsHeight - inset
			this.state.vx = 0
			this.state.vy = 0
			return
		}

		this.state.y = -this.state.radius * 0.35
	}

	private startLoop() {
		if (this.rafId) return
		this.lastFrameTime = undefined
		this.rafId = window.requestAnimationFrame(this.step)
	}

	private stopLoop() {
		if (!this.rafId) return
		window.cancelAnimationFrame(this.rafId)
		this.rafId = undefined
		this.lastFrameTime = undefined
	}

	private step = (timestamp: number) => {
		if (this.isDragging) {
			this.stopLoop()
			return
		}

		if (this.lastFrameTime === undefined) {
			this.lastFrameTime = timestamp
			this.rafId = window.requestAnimationFrame(this.step)
			return
		}

		const dt = Math.min((timestamp - this.lastFrameTime) / 1000, MAX_TIMESTEP)
		this.lastFrameTime = timestamp
		this.state.vy += this.gravity * dt
		this.state.vx *= AIR_DRAG
		this.state.x += this.state.vx * dt
		this.state.y += this.state.vy * dt
		this.state.rotation += this.getRotationDelta(this.state.vx * dt)
		this.resolveCollisions()
		this.render()

		if (this.shouldSettle()) {
			this.settle()
			return
		}

		this.rafId = window.requestAnimationFrame(this.step)
	}

	private resolveCollisions() {
		const minX = this.state.radius
		const maxX = this.boundsWidth - this.state.radius
		const minY = this.state.radius
		const maxY = this.boundsHeight - this.state.radius

		if (this.state.x < minX) {
			this.state.x = minX
			this.state.vx = Math.abs(this.state.vx) * this.bounce
		} else if (this.state.x > maxX) {
			this.state.x = maxX
			this.state.vx = -Math.abs(this.state.vx) * this.bounce
		}

		if (this.state.y < minY) {
			this.state.y = minY
			this.state.vy = Math.abs(this.state.vy) * this.bounce
		} else if (this.state.y > maxY) {
			this.state.y = maxY
			this.state.vy = -Math.abs(this.state.vy) * this.bounce
			this.state.vx *= this.friction * FLOOR_FRICTION
		}
	}

	private shouldSettle(): boolean {
		const floorY = this.boundsHeight - this.state.radius
		const isNearFloor = Math.abs(this.state.y - floorY) <= 0.5
		return (
			isNearFloor &&
			Math.abs(this.state.vx) <= SETTLE_SPEED &&
			Math.abs(this.state.vy) <= SETTLE_BOUNCE_SPEED
		)
	}

	private settle() {
		this.state.y = this.boundsHeight - this.state.radius
		this.state.vx = 0
		this.state.vy = 0
		this.render()
		this.stopLoop()
	}

	private render() {
		this.ballEl.style.transform = `translate3d(${this.state.x - this.state.radius}px, ${this.state.y - this.state.radius}px, 0) rotate(${this.state.rotation}deg)`
		if (this.hint.isVisible) {
			this.positionHint()
		}
	}

	private getRotationDelta(distance: number) {
		const circumference = Math.max(this.state.radius * Math.PI * 2, 1)
		return (distance / circumference) * 360
	}

	private clampCenterX(value: number) {
		return Math.min(
			this.boundsWidth - this.state.radius,
			Math.max(this.state.radius, value)
		)
	}

	private clampCenterY(value: number) {
		return Math.min(
			this.boundsHeight - this.state.radius,
			Math.max(this.state.radius, value)
		)
	}

	private getLocalPoint(event: PointerEvent) {
		const bounds = this.getBoundingClientRect()
		return {
			x: event.clientX - bounds.left,
			y: event.clientY - bounds.top,
		}
	}

	private pushPointerSample(sample: PointerSample) {
		this.pointerSamples.push(sample)
		const cutoff = sample.time - THROW_SAMPLE_WINDOW_MS
		while (this.pointerSamples.length > 1 && this.pointerSamples[0].time < cutoff) {
			this.pointerSamples.shift()
		}
	}

	private scheduleHint() {
		if (this.hasInteracted || this.hint.timeoutId) return
		this.hint.timeoutId = window.setTimeout(() => {
			this.hint.timeoutId = undefined
			if (this.hasInteracted) return
			this.showHint()
		}, HINT_DELAY_MS)
	}

	private clearHintTimeout() {
		if (this.hint.timeoutId === undefined) return
		window.clearTimeout(this.hint.timeoutId)
		this.hint.timeoutId = undefined
	}

	private showHint() {
		this.hint.isVisible = true
		this.hintEl.hidden = false
		this.positionHint()
	}

	private hideHint() {
		if (!this.hint.isVisible && this.hintEl.hidden) return
		this.hint.isVisible = false
		this.hintEl.hidden = true
	}

	private positionHint() {
		const hintWidth = this.hintEl.offsetWidth
		const hintHeight = this.hintEl.offsetHeight
		const hintMinX = MIN_VIEWPORT_INSET
		const hintMaxX = this.boundsWidth - hintWidth - MIN_VIEWPORT_INSET
		const nextX = this.clampValue(
			this.state.x - hintWidth / 2,
			hintMinX,
			hintMaxX
		)
		const nextY = this.getHintYPosition(hintHeight)
		this.hintEl.style.transform = `translate3d(${nextX}px, ${nextY}px, 0)`
	}

	private getHintYPosition(hintHeight: number) {
		const preferredY = this.state.y - this.state.radius - hintHeight - HINT_GAP
		if (preferredY >= MIN_VIEWPORT_INSET) {
			return preferredY
		}

		const maxY = this.boundsHeight - hintHeight - MIN_VIEWPORT_INSET
		return this.clampValue(
			this.state.y + this.state.radius + HINT_GAP,
			MIN_VIEWPORT_INSET,
			maxY
		)
	}

	private clampValue(value: number, min: number, max: number) {
		if (max < min) return min
		return Math.min(max, Math.max(min, value))
	}

	private handleFirstInteraction() {
		if (this.hasInteracted) return
		this.hasInteracted = true
		this.clearHintTimeout()
		this.hideHint()
	}

	private onPointerDown = (event: PointerEvent) => {
		if (event.pointerType === 'mouse' && event.button !== 0) return
		event.preventDefault()
		this.handleFirstInteraction()
		this.updateBounds()
		this.stopLoop()
		this.pointerId = event.pointerId
		this.isDragging = true
		const point = this.getLocalPoint(event)
		this.dragOffsetX = point.x - this.state.x
		this.dragOffsetY = point.y - this.state.y
		this.pointerSamples.length = 0
		this.pushPointerSample({
			x: this.state.x,
			y: this.state.y,
			time: performance.now(),
		})
		this.ballEl.setPointerCapture(event.pointerId)
	}

	private onPointerMove = (event: PointerEvent) => {
		if (!this.isDragging || event.pointerId !== this.pointerId) return
		const point = this.getLocalPoint(event)
		this.state.x = this.clampCenterX(point.x - this.dragOffsetX)
		this.state.y = this.clampCenterY(point.y - this.dragOffsetY)
		this.pushPointerSample({
			x: this.state.x,
			y: this.state.y,
			time: performance.now(),
		})
		this.render()
	}

	private onPointerUp = (event: PointerEvent) => {
		if (!this.isDragging || event.pointerId !== this.pointerId) return
		this.isDragging = false
		if (this.ballEl.hasPointerCapture(event.pointerId)) {
			this.ballEl.releasePointerCapture(event.pointerId)
		}
		this.pointerId = undefined

		const latestSample = this.pointerSamples[this.pointerSamples.length - 1]
		const earliestSample = this.pointerSamples[0]

		if (latestSample && earliestSample && latestSample.time > earliestSample.time) {
			const elapsedSeconds = (latestSample.time - earliestSample.time) / 1000
			const nextVx = (latestSample.x - earliestSample.x) / elapsedSeconds
			const nextVy = (latestSample.y - earliestSample.y) / elapsedSeconds
			this.state.vx = this.clampVelocity(nextVx)
			this.state.vy = this.clampVelocity(nextVy)
		} else {
			this.state.vx = 0
			this.state.vy = 0
		}

		this.pointerSamples.length = 0
		this.startLoop()
	}

	private clampVelocity(value: number) {
		return Math.min(MAX_RELEASE_SPEED, Math.max(-MAX_RELEASE_SPEED, value))
	}

	private onResize = () => {
		this.updateBounds()
		this.syncBallRadius()
		this.state.x = this.clampCenterX(this.state.x)
		this.state.y = this.clampCenterY(this.state.y)
		this.render()
	}
}
